import { useMemo } from 'react'
import { isResourceOfType, useAuthenticatedUser, useMe } from '@opallabs/api'
import { useWorkspace } from '@opallabs/hooks'
import {
  useIdle,
  usePresence,
  type PresenceUser,
} from '@opallabs/phoenix-websocket'

type PresenceViewer = {
  avatarUrl?: string
  isActive: boolean
  label: string
  uuid: string
}

// Converts from the presence user shape to the shape expected by `AvatarList`.
// Appends "(Idle)" to the user's label (name), which gets used by the avatar tooltip.
const toViewer = (user: PresenceUser): PresenceViewer => ({
  avatarUrl: user.avatar_url,
  isActive: !user.idle,
  label: user.idle ? `${user.name ?? ''} (Idle)` : (user.name ?? ''),
  uuid: user.id,
})

/**
 * Joins the presence room for an entity (or a whole collection when `entityId` is null) and
 * returns the other viewers as facepile user items. Yourself is filtered out, others are sorted
 * by name. The single source of presence logic shared by the in-app `LiveViewers` and the legacy
 * iframe `QueryableLiveViewers`.
 */
export const useLiveViewers = (
  entityType: string,
  entityId: string | null
): PresenceViewer[] => {
  const workspace = useWorkspace()
  const { data: user } = useAuthenticatedUser({
    routeWorkspaceSlug: workspace.slug,
  })
  const { data: opalUuid } = useMe({
    select: (document) =>
      document.included?.find(isResourceOfType('opal', 'V2'))?.attributes.uuid,
  })

  const userMeta = useMemo(
    () => ({
      avatar_url: user?.avatarAsset?.attributes.preview_url,
    }),
    [user?.avatarAsset]
  )
  const isIdle = useIdle()

  // Room = the entity (with id) or the whole collection (without). The topic stays empty
  // until the Opal + workspace are known; usePresence treats "" as "don't join yet".
  const room = entityId ? `${entityType}:${entityId}` : entityType
  const topic =
    opalUuid && workspace.uuid
      ? `presence:${opalUuid}:${workspace.uuid}:${room}`
      : ''
  const presentUsers = usePresence(topic, userMeta, isIdle)

  return useMemo(
    () =>
      presentUsers
        .reduce<PresenceViewer[]>((viewers, presentUser) => {
          if (presentUser.id !== user?.uuid) viewers.push(toViewer(presentUser))
          return viewers
        }, [])
        .sort((a, b) => a.label.localeCompare(b.label)),
    [presentUsers, user?.uuid]
  )
}
